/**
 * A page open across a deploy.
 *
 * Every route is a lazy chunk with a content hash in its name, and a deploy replaces the whole
 * set. A tab opened before it still holds the old index, so the first route it has not visited
 * yet asks for a chunk that no longer exists, and the import fails. Nothing is broken except
 * that tab: loading the page again fetches the new index, which names the new chunks.
 *
 * So a failed chunk is answered by reloading once, at the address the reader was going to. The
 * guard is per address and short-lived, so a chunk that is genuinely missing from the new build
 * too (or a connection that is down) shows the error screen instead of reloading forever.
 */

const KEY = 'ee-stale-reload';
const WINDOW_MS = 10_000;

/** The messages each engine gives a dynamic import that could not be fetched or evaluated. */
const STALE = [
  'Failed to fetch dynamically imported module', // Chromium
  'error loading dynamically imported module', // Firefox
  'Importing a module script failed', // Safari
  'is not a valid JavaScript MIME type', // Safari, served the index instead of the chunk
  'Unable to preload CSS', // Vite's own preload helper
];

export function isStaleBuildError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  return STALE.some((needle) => message.includes(needle));
}

/**
 * Reloads unless this address was reloaded for the same reason a moment ago. Answers whether it
 * did, so the caller can show its error instead.
 */
export function reloadForNewBuild(): boolean {
  const here = location.pathname + location.search;
  try {
    const last = JSON.parse(sessionStorage.getItem(KEY) ?? 'null') as { at: number; here: string } | null;
    if (last && last.here === here && Date.now() - last.at < WINDOW_MS) return false;
    sessionStorage.setItem(KEY, JSON.stringify({ at: Date.now(), here }));
  } catch {
    // No storage, so no guard: reload anyway, which is right far more often than it loops.
  }
  location.reload();
  return true;
}

/** Vite's preload helper reports a failed chunk on window before the import rejects. */
export function listenForStaleBuild(): void {
  window.addEventListener('vite:preloadError', (event) => {
    if (reloadForNewBuild()) event.preventDefault();
  });
}
